import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Camera,
  Heart,
  Star,
  Sparkles
} from 'lucide-react';
import { galleryImages } from '../data/mock';

const GalleryPage = () => {
  const galleryData = [
    {
      id: 1,
      title: 'Car-shaped Trolleys',
      description: 'Our signature ride-on shopping trolleys that kids love',
      emoji: '🚗',
      color: 'bg-[hsl(210,70%,90%)]',
    },
    {
      id: 2,
      title: 'Branded Jolly Bays',
      description: 'Our colorful pickup stations in participating malls',
      emoji: '🏪',
      color: 'bg-[hsl(33,100%,90%)]',
    },
    {
      id: 3,
      title: 'Road-themed Decor',
      description: 'Fun road and car motifs throughout our stations',
      emoji: '🛣️',
      color: 'bg-[hsl(45,100%,90%)]',
    },
    {
      id: 4,
      title: 'Photo Booth Moments',
      description: 'Capture memories of your shopping adventure',
      emoji: '📸',
      color: 'bg-[hsl(300,60%,92%)]',
    },
    {
      id: 5,
      title: 'Seasonal Events',
      description: 'Special themed experiences during holidays',
      emoji: '🎉',
      color: 'bg-[hsl(0,70%,92%)]',
    },
    {
      id: 6,
      title: 'Happy Families',
      description: 'Creating joyful shopping memories every day',
      emoji: '👨‍👩‍👧',
      color: 'bg-[hsl(120,50%,90%)]',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden jolly-hero-gradient py-16 md:py-24">
        <div className="absolute top-10 right-10 w-20 h-20 rounded-full bg-[hsl(45,98%,80%)] opacity-50 sun-pulse" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold jolly-text-teal mb-6">
              The Jolly Experience
            </h1>
            <p className="text-gray-600 text-lg md:text-xl">
              A glimpse into the joy, fun, and memories created at Jolly Tots Rides.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {galleryData.map((item, index) => (
              <Card 
                key={item.id} 
                className="gallery-item jolly-card overflow-hidden card-enter"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`aspect-[4/3] ${item.color} flex items-center justify-center`}>
                  <span className="text-7xl">{item.emoji}</span>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold jolly-text-teal mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Booth Section */}
      <section className="py-16 md:py-24 jolly-gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="jolly-section-title">
                Capture the Magic
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Every Jolly Tots Ride is an opportunity to create lasting memories. Many families capture photos of their little ones enjoying the ride — a moment of joy that lasts beyond the shopping trip.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Camera className="w-5 h-5 jolly-text-orange" />
                  <span className="text-gray-700 font-medium">Photo Moments</span>
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  <span className="text-gray-700 font-medium">Happy Memories</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <div className="text-center space-y-4">
                <div className="inline-block p-6 bg-[hsl(45,100%,90%)] rounded-full">
                  <Camera className="w-12 h-12 jolly-text-orange" />
                </div>
                <h3 className="text-xl font-bold jolly-text-teal">Photo Booth Available!</h3>
                <p className="text-gray-600">
                  Dedicated photo spots at select Jolly Bays for capturing family moments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mascot Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="jolly-section-title mb-8">Meet Jolly Bear!</h2>
          
          <div className="bg-gradient-to-br from-[hsl(210,70%,92%)] to-[hsl(210,60%,85%)] rounded-3xl p-12 inline-block">
            <img 
              src="https://customer-assets.emergentagent.com/job_f4ef9e04-9139-4323-b854-e5d21a16af08/artifacts/l5hnk185_Jolly%20Bear%201.png"
              alt="Jolly Bear Mascot"
              className="w-48 h-auto md:w-56 mx-auto"
            />
          </div>
          
          <p className="mt-8 text-gray-600 text-lg max-w-xl mx-auto">
            Our friendly mascot Jolly Bear makes special appearances at participating malls during holidays and special events. Keep an eye out!
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[hsl(33,100%,50%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Create Your Own Jolly Moments
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Visit a Jolly Tots Ride today and start making memories!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/parents">
              <Button className="bg-white text-[hsl(33,100%,40%)] hover:bg-gray-100 font-bold rounded-full px-8 py-4 text-base shadow-lg">
                <Sparkles className="w-5 h-5 mr-2" />
                Find a Location
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
